/**
 * Modern Notification System for InterPals
 * Clean, minimal design inspired by Airbnb/Duolingo
 * Uses vanilla JS - no jQuery dependency
 */

const NotificationConfig = {
  DURATION: {
    ERROR: 5000,
    SUCCESS: 4000,
    WARNING: 5000,
    INFO: 4000
  },
  MAX_NOTIFICATIONS: 3,
  ANIMATION_DURATION: 200,
  Z_INDEX: 9999,
  // Time before toast ends to trigger follow-up actions (e.g., page reload)
  ACTION_BUFFER: 500
};

const NotificationType = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info'
};

// Expose config globally for other scripts
window.NotificationConfig = NotificationConfig;

class NotificationManager {
  constructor() {
    this.notifications = [];
    this.container = null;
    this.init();
  }

  init() {
    this.createContainer();
    this.addStyles();
  }

  createContainer() {
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.id = 'notification-container';
    this.container.className = 'fixed inset-x-0 top-0 sm:inset-auto sm:bottom-5 sm:left-5 flex flex-col items-center sm:items-start gap-2 pointer-events-none p-3 sm:p-0';
    this.container.setAttribute('role', 'region');
    this.container.setAttribute('aria-label', 'Notifications');
    this.container.setAttribute('aria-live', 'polite');
    this.container.style.zIndex = NotificationConfig.Z_INDEX;

    document.body.appendChild(this.container);
  }

  addStyles() {
    if (document.getElementById('notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'notification-styles';
    style.textContent = `
      .ip-toast {
        pointer-events: auto;
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
        transition: all ${NotificationConfig.ANIMATION_DURATION}ms cubic-bezier(0.2, 0, 0, 1);
      }

      .ip-toast.show {
        opacity: 1;
        transform: translateY(0) scale(1);
      }

      .ip-toast.hide {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
      }

      @media (min-width: 640px) {
        .ip-toast {
          transform: translateX(-12px) scale(0.98);
        }

        .ip-toast.show {
          transform: translateX(0) scale(1);
        }

        .ip-toast.hide {
          transform: translateX(-12px) scale(0.98);
        }
      }
    `;

    document.head.appendChild(style);
  }

  show(message, type = NotificationType.INFO, options = {}) {
    this.pruneNotifications();

    const notification = this.createNotification(message, type, options);
    this.notifications.push(notification);

    this.container.appendChild(notification.element);

    // Initialize Lucide icons after element is in the DOM
    if (window.LucideIcons) {
      window.LucideIcons.initialize(notification.element);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        notification.element.classList.add('show');
        this.checkMultiline(notification.element);
      });
    });

    if (options.autoHide !== false) {
      const duration = options.duration || NotificationConfig.DURATION[type.toUpperCase()] || 4000;
      notification.timeout = setTimeout(() => this.hide(notification), duration);
    }

    return notification;
  }

  createNotification(message, type, options) {
    const id = 'toast-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    const config = this.getTypeConfig(type);

    const element = document.createElement('div');
    element.id = id;
    element.className = 'ip-toast w-full max-w-xs sm:max-w-sm';
    element.dataset.type = type;
    element.setAttribute('role', 'alert');

    element.innerHTML = `
      <div class="ip-toast-inner flex items-center gap-3 px-4 py-3 ${config.bg} shadow-lg rounded-full">
        <i data-lucide="${config.icon}" class="flex-shrink-0 w-5 h-5 text-white"></i>
        <p class="flex-1 text-sm font-semibold text-white break-words">${message}</p>
        <button type="button" class="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" aria-label="Dismiss">
          <i data-lucide="x" class="w-3.5 h-3.5 text-white"></i>
        </button>
      </div>
    `;

    element.querySelector('button').addEventListener('click', () => this.hide({ id, element }));

    return { id, element, type, message };
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  checkMultiline(element) {
    const textEl = element.querySelector('p');
    if (!textEl) return;

    const lineHeight = parseFloat(getComputedStyle(textEl).lineHeight) || 20;
    const height = textEl.getBoundingClientRect().height;

    if (height > lineHeight * 1.5) {
      const inner = element.querySelector('.ip-toast-inner');
      if (!inner) return;
      inner.classList.remove('rounded-full', 'items-center');
      inner.classList.add('rounded-2xl', 'items-start');
    }
  }

  getTypeConfig(type) {
    const configs = {
      success: {
        bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
        icon: 'check'
      },
      error: {
        bg: 'bg-gradient-to-r from-rose-500 to-pink-500',
        icon: 'triangle-alert'
      },
      warning: {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: 'triangle-alert'
      },
      info: {
        bg: 'bg-gradient-to-r from-sky-500 to-blue-500',
        icon: 'lightbulb'
      }
    };
    return configs[type] || configs.info;
  }

  hide(notification) {
    if (!notification?.element) return;

    if (notification.timeout) clearTimeout(notification.timeout);

    notification.element.classList.remove('show');
    notification.element.classList.add('hide');

    setTimeout(() => {
      notification.element.remove();
      this.notifications = this.notifications.filter(n => n.id !== notification.id);
    }, NotificationConfig.ANIMATION_DURATION);
  }

  pruneNotifications() {
    while (this.notifications.length >= NotificationConfig.MAX_NOTIFICATIONS) {
      const oldest = this.notifications.shift();
      if (oldest) this.hide(oldest);
    }
  }

  clearAll() {
    this.notifications.forEach(n => this.hide(n));
  }
}

let notificationManager;

function initNotificationSystem() {
  if (!notificationManager) {
    notificationManager = new NotificationManager();
  }
  return notificationManager;
}

window.showNotification = function(message, type, options) {
  if (!notificationManager) notificationManager = initNotificationSystem();
  return notificationManager.show(message, type, options);
};

window.showError = (message, options) => window.showNotification(message, NotificationType.ERROR, options);
window.showSuccess = (message, options) => window.showNotification(message, NotificationType.SUCCESS, options);
window.showWarning = (message, options) => window.showNotification(message, NotificationType.WARNING, options);
window.showInfo = (message, options) => window.showNotification(message, NotificationType.INFO, options);

/**
 * Rich push notification with avatar, title, body text, and clickable link.
 * Used by the WebSocket system for new messages, friend requests, etc.
 */
window.showPushNotification = function({ url, username, title, text, img_url }) {
  if (!notificationManager) notificationManager = initNotificationSystem();
  notificationManager.pruneNotifications();

  const id = 'toast-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
  const element = document.createElement('div');
  element.id = id;
  element.className = 'ip-toast w-full max-w-xs sm:max-w-sm';
  element.dataset.type = 'push';
  element.setAttribute('role', 'alert');

  const esc = (s) => {
    const d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  };
  const safeTitle = esc(title);
  const safeText = esc(text);
  const avatarUrl = esc(img_url) || '//ipstatic.net/images/male-empty-profile.png';
  const profileUrl = username ? '/' + encodeURIComponent(username) : '#';
  const linkUrl = url ? esc(url) : profileUrl;

  element.innerHTML = `
    <a href="${linkUrl}" class="ip-toast-inner flex items-center gap-3 px-4 py-3 bg-gray-900/95 backdrop-blur shadow-lg rounded-2xl no-underline cursor-pointer group">
      <img src="${avatarUrl}" alt="" class="w-10 h-10 rounded-full object-cover flex-shrink-0">
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-white truncate">${safeTitle}</p>
        ${safeText ? `<p class="text-xs text-gray-300 truncate mt-0.5">${safeText}</p>` : ''}
      </div>
      <button type="button" class="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors ip-toast-close" aria-label="Dismiss">
        <i data-lucide="x" class="w-3.5 h-3.5 text-white"></i>
      </button>
    </a>
  `;

  const notification = { id, element, type: 'push', message: safeTitle };

  element.querySelector('.ip-toast-close').addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    notificationManager.hide(notification);
  });

  notificationManager.notifications.push(notification);
  notificationManager.container.appendChild(element);

  if (window.LucideIcons) {
    window.LucideIcons.initialize(element);
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.classList.add('show');
    });
  });

  notification.timeout = setTimeout(() => notificationManager.hide(notification), 6000);

  return notification;
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initNotificationSystem());
} else {
  initNotificationSystem();
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { NotificationManager, NotificationConfig, NotificationType, initNotificationSystem };
}
